import { useEffect, useState, useCallback } from 'react';
import { useMainCompany } from '../../context/MainCompanyContext';
import api from '../../api/axios';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import Toast from '../../components/common/Toast';

export default function TransferTitleRep() {
  const { selectedMain } = useMainCompany();
  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [reps, setReps] = useState([]);
  const [repsTo, setRepsTo] = useState([]);

  const [stateId, setStateId] = useState(0);
  const [countyId, setCountyId] = useState(0);
  const [repFrom, setRepFrom] = useState(0);
  const [repTo, setRepTo] = useState(0);

  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [, setLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', visible: false, type: 'success' });

  // move modal state
  const [showMoveConfirm, setShowMoveConfirm] = useState(false);
  const [movePassword, setMovePassword] = useState('');
  const [moving, setMoving] = useState(false);

  const showToast = (message, type = 'success', duration = 3500) => {
    setToast({ message, visible: true, type });
    setTimeout(() => setToast({ message: '', visible: false, type }), duration);
  };

  // load states on mount and whenever the selected main company changes
  useEffect(() => {
    let mounted = true;
    // clear dependent filters when main company changes
    setStateId(0);
    setCountyId(0);
    setRepFrom(0);
    setRepTo(0);
    setReps([]);
    setRepsTo([]);

    const params = {};
    if (selectedMain) params.maincompanyid = selectedMain;
    api.get('/admin/states', { params }).then((resp) => {
      const body = resp.data;
      const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
      if (mounted) setStates(items);
    }).catch(() => setStates([]));
    return () => { mounted = false; };
  }, [selectedMain]);

  // load counties when state changes
  useEffect(() => {
    let mounted = true;
    if (!stateId) {
      setCounties([]);
      setCountyId(0);
      return;
    }
    const params = { stateId };
    if (selectedMain) params.maincompanyid = selectedMain;
    api.get('/auth/counties', { params }).then((resp) => {
      const body = resp.data;
      const items = Array.isArray(body) ? body : (Array.isArray(body.data) ? body.data : []);
      if (mounted) setCounties(items);
    }).catch(() => { if (mounted) setCounties([]); });
    return () => { mounted = false; };
  }, [stateId, selectedMain]);

  // load title reps for filter (from) when state/county change
  // load title reps for both 'from' and 'to' filters when state/county (or company) change
  useEffect(() => {
    let mounted = true;
    if (!stateId || !countyId) {
      setReps([]);
      setRepsTo([]);
      setRepFrom(0);
      setRepTo(0);
      return () => { mounted = false; };
    }
    const params = { stateId, countyId };
    if (selectedMain) params.maincompanyid = selectedMain;

    api.get('/auth/titlereps', { params }).then((resp) => {
      const items = resp.data && (Array.isArray(resp.data) ? resp.data : resp.data.data) ? (Array.isArray(resp.data) ? resp.data : resp.data.data) : [];
      if (mounted) {
        setReps(items);
        setRepsTo(items);
      }
    }).catch(() => { if (mounted) { setReps([]); setRepsTo([]); } });

    return () => { mounted = false; };
  }, [stateId, countyId, selectedMain]);

  // fetch users list with filters + pagination
  const fetchUsers = useCallback(
    async (p = 1, lim = limit) => {
      try {
        setLoading(true);
        const params = {};
        if (stateId) params.stateid = stateId;
        if (countyId) params.countyid = countyId;
        if (repFrom) params.titlerep_id = repFrom;
        params.page = Number(p) || 1;
        params.limit = Number(lim) || 10;
        if (selectedMain) params.maincompanyid = selectedMain;

        const resp = await api.get("/admin/transfer-title-rep/users", { params });
        const body = resp.data;
        const list = body?.data || [];
        const meta = body.pagination || body.meta || { total: list.length, page: params.page, limit: params.limit };
        setUsers(list);
        setTotal(Number(meta.total) || 0);
        setPage(Number(meta.page) || params.page);
        setLimit(Number(meta.limit) || params.limit);
      } catch (err) {
        console.error("Failed to load users", err);
        showToast(err?.response?.data?.message || "Failed to load users", "error");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [stateId, countyId, repFrom, selectedMain, limit] // keep it stable!
  );


  // initial fetch is handled by the specific effects below (on mount and when filters change)

  // when main company changes in header, refresh users for new main company
  useEffect(() => {
    setSelected([]);
    setPage(1);
  //  fetchUsers(1);
  }, [selectedMain]);

  // when filters change, reset dependent selections and fetch
  useEffect(() => {
    setSelected([]);
    setRepFrom(0);
    setRepTo(0);
    setPage(1);
  //  fetchUsers(1);
  }, [stateId, countyId]);

  useEffect(() => {
    setSelected([]);
    setRepFrom(repFrom);
    setPage(1);
    fetchUsers(1);
  }, [repFrom, fetchUsers]);

  // checkbox logic
  const toggleSelect = (id) => {
    if (selected.includes(id)) setSelected(selected.filter((s) => s !== id));
    else setSelected([...selected, id]);
  };
  const toggleSelectAll = () => {
    if (!users || !users.length) return;
    const ids = users.map((u) => u.id);
    if (selected.length === ids.length) setSelected([]);
    else setSelected(ids);
  };

  const canMove = selected.length > 0 && Number(repTo) > 0;

  // open modal instead of prompt
  const doMove = () => {
    setMovePassword('');
    setShowMoveConfirm(true);
  };

  const performMove = async () => {
    if (!movePassword) return;
    setMoving(true);
    try {
      const payload = { user_id: selected, title_rep_id: Number(repTo), move_password: movePassword };
      const resp = await api.put('/admin/transfer-title-rep/update', payload, { skipAuthRedirect: true });
      if (resp.data && resp.data.status === 'success') {
        showToast(resp.data.message || 'Users updated', 'success');
        setSelected([]);
        setShowMoveConfirm(false);
        setMovePassword('');
        fetchUsers(page);
      } else {
        showToast(resp.data.message || 'Update failed', 'error');
      }
    } catch (err) {
      showToast(err?.response?.data?.message || 'Update failed', 'error');
    } finally {
      setMoving(false);
    }
  };

  // pagination helper (server-driven pagination shown inline)

  return (
    <div className="p-4 admin-company-page">
      <Toast message={toast.message} visible={toast.visible} type={toast.type} />

      <AdminPageHeader
        title="Transfer Title Rep"
        filters={(
          <div className="flex gap-2 items-center">
            <select value={stateId} onChange={(e) => { setStateId(Number(e.target.value) || 0); setCountyId(0); }} className="border px-2 py-2 rounded text-sm bg-white text-black w-56">
              <option value={0}>Select State</option>
              {states.map((s) => (
                <option key={s.id || s.stateid || s.value} value={s.id || s.stateid || s.value}>{s.statename || s.name}</option>
              ))}
            </select>

            <select value={countyId} onChange={(e) => { setCountyId(Number(e.target.value) || 0); }} className="border px-2 py-2 rounded text-sm bg-white text-black w-56">
              <option value={0}>All Counties</option>
              {counties.map((c) => (
                <option key={c.id || c.countyid || c.value} value={c.id || c.countyid || c.value}>{c.countyname || c.name}</option>
              ))}
            </select>

            <select value={repFrom} onChange={(e) => setRepFrom(Number(e.target.value) || 0)} className="border px-2 py-2 rounded text-sm bg-white text-black w-56">
              <option value={0}>All Title Reps (from)</option>
              {reps.map((r) => (
                <option key={r.id} value={r.id}>{r.name || (r.firstname && r.lastname ? `${r.firstname} ${r.lastname}` : r.name)}</option>
              ))}
            </select>

            <span className="text-sm text-gray-600">to</span>

            <select value={repTo} onChange={(e) => setRepTo(Number(e.target.value) || 0)} className="border px-2 py-2 rounded text-sm bg-white text-black w-56">
              <option value={0}>Select Title Rep (to)</option>
              {repsTo.map((r) => (
                <option key={r.id} value={r.id}>{r.name || (r.firstname && r.lastname ? `${r.firstname} ${r.lastname}` : r.name)}</option>
              ))}
            </select>

            <button disabled={!canMove} onClick={doMove} className={`px-3 py-2 text-sm rounded text-white ${!canMove ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
              Move
            </button>
          </div>
        )}
      />

      {/* Move confirm modal */}
      {showMoveConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
          <div className="bg-white rounded shadow-lg w-full max-w-sm p-4">
            <h3 className="text-lg font-semibold">Confirm Move</h3>
            <p className="mt-2">Enter the move password to confirm transferring selected users to the chosen Title Rep.</p>
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <div className="mt-3">
                <label className="block text-sm">Move Password</label>
                <input
                  type="password"
                  name={`move-password`}
                  autoComplete="new-password"
                  spellCheck="false"
                  autoCorrect="off"
                  value={movePassword}
                  onChange={(e) => setMovePassword(e.target.value)}
                  className="w-full border px-2 py-1 rounded mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => { setShowMoveConfirm(false); setMovePassword(''); }} className="px-3 py-1 border rounded">Cancel</button>
                <button type="button" disabled={!movePassword || moving} onClick={performMove} className="px-3 py-1 bg-blue-600 text-white rounded">{moving ? 'Moving...' : 'Move'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-2 py-2"><input type="checkbox" checked={selected.length === users.length && users.length > 0} onChange={toggleSelectAll} /></th>
              <th className="px-2 py-2 text-left">Name</th>
              <th className="px-2 py-2 text-left">Company</th>
              <th className="px-2 py-2 text-left">Username</th>
              <th className="px-2 py-2 text-left">Email</th>
              <th className="px-2 py-2 text-left">Title Rep</th>
            </tr>
          </thead>
          <tbody>
            {!users || users.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-6 text-gray-500">No results</td></tr>
            ) : users.map((u, idx) => {
              const isSelected = selected.includes(u.id);
              return (
                <tr key={u.id} className={`${isSelected ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100`}>
                  <td className="px-2 py-2"><input type="checkbox" checked={isSelected} onChange={() => toggleSelect(u.id)} /></td>
                  <td className="px-2 py-2">{(u.first_name || u.last_name) ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.name || ''}</td>
                  <td className="px-2 py-2">{u.company_name || u.company}</td>
                  <td className="px-2 py-2">{u.username}</td>
                  <td className="px-2 py-2 truncate" title={u.email}>{u.email}</td>
                  <td className="px-2 py-2">{(u.titlerep_firstname || u.titlerep_lastname) ? `${u.titlerep_firstname || ''} ${u.titlerep_lastname || ''}`.trim() : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center gap-3 mt-3 text-sm">
        <span>Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total || users.length)} of {total || users.length}</span>
        <div className="flex items-center gap-2">
          <label className="text-gray-600">Rows</label>
          <select value={limit} onChange={(e) => { const l = Number(e.target.value) || 10; setLimit(l); fetchUsers(1, l); }} className="border rounded px-2 py-1">
            {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <button onClick={() => { if (page > 1) { fetchUsers(page - 1); } }} disabled={page === 1} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">Prev</button>
          <span>{page} / {Math.max(1, Math.ceil((total || users.length) / limit))}</span>
          <button onClick={() => { const last = Math.max(1, Math.ceil((total || users.length) / limit)); if (page < last) fetchUsers(page + 1); }} disabled={page >= Math.max(1, Math.ceil((total || users.length) / limit))} className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100">Next</button>
        </div>
      </div>

    </div>
  );
}
